import { SvgIcon, SxProps, Tab, tabClasses, Tabs, tabsClasses, Theme, Typography } from '@mui/material';
import { Link } from 'react-router';
import { useEffect } from 'react';
import { useAtom } from 'jotai';
import useNavigationTabs, { NavigationTabInfo } from '../hooks/useNavigationTabs';
import { RoutePaths } from '../routes/AppRoutes';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { SNACKBAR_DEFAULT_HIDE_DURATION } from '../state';
import { getStateLayerColor, StateLayer } from '../theme/color.utils';
import useCurrentTabs from './hooks/useCurrentTab';

export interface NavigationTabsProps {
  sx?: SxProps<Theme>;
}

export default function NavigationTabs({ sx }: NavigationTabsProps): React.JSX.Element {
  const tabsInfo = useNavigationTabs();
  const currentTabValue = useCurrentTabs([
    RoutePaths.DISCOVERY_SUPPLIERS,
    RoutePaths.DISCOVERY_BUYERS,
    RoutePaths.SEARCH_PRODUCT,
    RoutePaths.CONTRACT_LIST,
    RoutePaths.ORDER_LIST,
  ]);
  const [commonSnackbarProps, setCommonSnackbarProps] = useAtom(commonSnackbarPropsAtom);
  const getTabColor = (
    tabValue: NavigationTabInfo,
    selectedColor: string,
    defaultColor: string | undefined,
  ): string | undefined => {
    return tabValue.to === currentTabValue ? selectedColor : defaultColor;
  };

  useEffect(() => {
    if (commonSnackbarProps && !commonSnackbarProps!.snackbarProps.autoHideDuration) {
      setCommonSnackbarProps({
        ...commonSnackbarProps,
        snackbarProps: {
          ...commonSnackbarProps.snackbarProps,
          autoHideDuration: SNACKBAR_DEFAULT_HIDE_DURATION,
        },
      });
    }
  }, [currentTabValue]);

  return (
    <Tabs
      centered
      sx={{
        ...sx,
        [`& .${tabsClasses.indicator}`]: {
          display: 'none',
        },
        [`& .${tabsClasses.centered}`]: {
          gap: 1,
        },
        flex: { lg: 1 },
      }}
      textColor="secondary"
      value={currentTabValue}
    >
      {tabsInfo.map((tab) => (
        <Tab
          key={tab.to}
          value={tab.to}
          disableRipple
          icon={(
            <SvgIcon
              sx={{
                px: 2.5,
                py: 0.5,
                borderRadius: '16px',
                color: getTabColor(tab, 'onSecondaryContainer.main', undefined),
                backgroundColor: getTabColor(tab, 'onSecondaryContainer.contrastText', undefined),
              }}
            >
              <tab.icon />
            </SvgIcon>
          )}
          label={<Typography variant="labelMedium" color={getTabColor(tab, 'onSurface.main', undefined)}>{tab.label}</Typography>}
          component={Link}
          to={tab.to}
          sx={(theme) => ({
            flex: 1,
            fontSize: '0.875rem',
            py: 1.5,
            px: 0,
            maxWidth: 100,
            '&:hover': {
              [`& .${tabClasses.icon}`]: {
                backgroundColor: getStateLayerColor(StateLayer.Layer8, getTabColor(tab, theme.palette.secondaryContainer.main, theme.palette.surfaceContainerLowest.main)!, getTabColor(tab, theme.palette.onSecondaryContainer.main, theme.palette.onSurfaceVariant.main)!),
              },
            },
            '&:focus': {
              [`& .${tabClasses.icon}`]: {
                backgroundColor: tab.to === currentTabValue ? 'onSecondaryContainer.contrastText' : getStateLayerColor(StateLayer.Layer12, theme.palette.surfaceContainerLowest.main, theme.palette.onSurfaceVariant.main),
              },
            },
            [`& .${tabClasses.icon}`]: {
              mb: 0.5,
            },
            [`& .${tabClasses.selected}`]: {
              fontWeight: 600,
              color: 'secondary.main',
            },
            [`& .${tabClasses.root}`]: {
              flex: 1,
            },
          })}
        />
      ))}
    </Tabs>
  );
}
